import {
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useStepperContext } from "app/contexts/stepper";
import { useEffect, useState } from "react";
import { useResourcesContext } from "app/contexts/resources";
import { coordenadasRegex } from "app/utils/regexps";
import { toast } from "react-hot-toast";
import { useInscripcionContext } from "../../inscripcion-context";

const initialState = {
  ciudad_id: 1,
  establecimiento_direccion_detallada: "",
  establecimiento_coordenadas: "",
  establecimiento_principal: false,
};

const initialDepto = {
  departamento_id: 0,
  ciudades: [],
};

const initialCity = {
  ciudad_id: 0,
  ciudad_matricula_requerida: 0,
};

function EstablecimientoForm({ establecimiento = initialState }) {
  const {
    departamentosDeHonduras,
    getDepartamentoByCiudadId,
    getCiudad,
    getDepartamento,
  } = useResourcesContext();

  const { addEstablecimiento, updateEstablecimiento, deleteEstablecimiento } =
    useInscripcionContext();

  const [state, setState] = useState(establecimiento);
  const [isEditable] = useState(!!establecimiento.establecimiento_id);
  const [departamento, setDepartamento] = useState(initialDepto);
  const [ciudad, setCiudad] = useState(initialCity);
  const [isEditing, setEditing] = useState(false);

  useEffect(() => {
    // si existe el id de la ciudad que seleccione el departamento
    if (!!state.ciudad_id) {
      const depto = getDepartamentoByCiudadId(state.ciudad_id);

      setDepartamento(depto);

      const ciudad = getCiudad(state.ciudad_id);

      setCiudad(ciudad);
    }
  }, [state.ciudad_id, getDepartamentoByCiudadId, getCiudad]);

  function handleSubmit(e) {
    e.preventDefault();

    try {
      if (!!isEditable && !isEditing) {
        return setEditing(true);
      } else if (!!isEditable && !!isEditing) {
        const isUpdated = updateEstablecimiento({
          establecimiento: state,
        });

        if (isUpdated) {
          setEditing(false);

          return toast.success("¡Establecimiento actualizado!");
        }
      } else {
        const isAdded = addEstablecimiento({ establecimiento: state });

        if (isAdded) {
          setState(establecimiento);

          // setDepartamento(initialDepto);

          // setCiudad(initialCity);

          return toast.success("¡Establecimiento agregado!");
        }
      }
    } catch (error) {
      console.log(error.message);

      return toast.error(error.message);
    }
  }

  function handleReset() {
    if (!!isEditable && !isEditing) {
      const isDeleted = deleteEstablecimiento({
        establecimiento_id: establecimiento.establecimiento_id,
      });

      if (isDeleted) {
        return toast.success("¡Establecimiento eliminado!");
      }
    } else if (!!isEditable && !!isEditing) {
      const isCanceled = window.confirm("¿Desea cancelar la edición?");

      if (isCanceled) {
        setState(establecimiento);

        return setEditing(false);
      }
    } else {
      const isReseted = window.confirm("¿Desea reiniciar el formulario?");

      if (isReseted) {
        setState(establecimiento);

        // setDepartamento(initialDepto);

        // setCiudad(initialCity);
      }
    }
  }

  return (
    <Paper
      component="form"
      className="animate__animated animate__slideInUp"
      onSubmit={handleSubmit}
      onReset={handleReset}
      variant="outlined"
    >
      <Grid container spacing={1} sx={{ padding: 2 }}>
        <Grid item xs={12}>
          <Typography variant="overline">
            <strong>
              {`${!!isEditable ? "Editar" : "Agregar"} establecimiento`}
            </strong>
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl size="small" fullWidth required>
            <InputLabel id="departamento-label">Departamento</InputLabel>

            <Select
              label="Departamento"
              labelId="departamento-label"
              value={departamento.departamento_id || ""}
              onChange={(e) => {
                const depto = getDepartamento(e.target.value);

                setDepartamento(depto);

                setState((state) => ({
                  ...state,
                  ciudad_id: 0,
                }));

                setCiudad((ciudad) => ({
                  ...ciudad,
                  ciudad_id: 0,
                }));
              }}
              disabled={!!isEditable && !isEditing}
            >
              {departamentosDeHonduras.results.map((departamento) => (
                <MenuItem
                  key={departamento.departamento_id}
                  value={departamento.departamento_id}
                >
                  {departamento.departamento_nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl
            size="small"
            fullWidth
            required
            disabled={!departamento.departamento_id}
          >
            <InputLabel id="municipio-label">Municipio</InputLabel>

            <Select
              label="Municipio"
              labelId="municipio-label"
              value={ciudad.ciudad_id || ""}
              onChange={(e) => {
                setState((state) => ({
                  ...state,
                  ciudad_id: e.target.value,
                }));
              }}
              disabled={
                (!!isEditable && !isEditing) || !departamento.departamento_id
              }
            >
              {departamento.ciudades.map((ciudad) => (
                <MenuItem key={ciudad.ciudad_id} value={ciudad.ciudad_id}>
                  {ciudad.ciudad_nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            type="text"
            label="Dirección detallada"
            value={state.establecimiento_direccion_detallada}
            onChange={(e) =>
              setState((state) => ({
                ...state,
                establecimiento_direccion_detallada: e.target.value,
              }))
            }
            size="small"
            fullWidth
            multiline
            rows={2}
            required
            disabled={!!isEditable && !isEditing}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            type="text"
            label="Coordenadas (opcional)"
            value={state.establecimiento_coordenadas}
            onChange={(e) =>
              setState((state) => ({
                ...state,
                establecimiento_coordenadas: e.target.value,
              }))
            }
            inputProps={coordenadasRegex}
            size="small"
            fullWidth
            disabled={!!isEditable && !isEditing}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormControlLabel
              disabled={!!isEditable && !isEditing}
              control={
                <Checkbox
                  checked={state.establecimiento_principal}
                  onChange={(e) =>
                    setState((state) => ({
                      ...state,
                      establecimiento_principal: e.target.checked,
                    }))
                  }
                />
              }
              label="¿Es un establecimiento principal?"
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack direction="row" justifyContent="end" spacing={1}>
            <Button
              type="submit"
              variant="contained"
              size="small"
              color={!!isEditable ? "warning" : "success"}
            >
              {!!isEditable && !isEditing
                ? "Editar"
                : !!isEditing
                ? "Actualizar"
                : "Agregar"}
            </Button>

            <Button
              type="reset"
              variant="outlined"
              size="small"
              color={!!isEditable && !isEditing ? "error" : "inherit"}
            >
              {!!isEditable && !isEditing
                ? "Eliminar"
                : !!isEditing
                ? "Cancelar"
                : "Reiniciar"}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

export function Establecimientos() {
  const stepper = useStepperContext();
  const { empresa } = useInscripcionContext();

  function handleNext() {
    try {
      if (!empresa.establecimientos.length) {
        throw new Error("¡Por favor, agregue al menos un establecimiento!");
      }

      return stepper.next();
    } catch (error) {
      return toast.error(error.message);
    }
  }

  return (
    <Stack spacing={2}>
      <Container maxWidth="sm">
        <Stack spacing={2}>
          {empresa.establecimientos.map((establecimiento) => (
            <EstablecimientoForm
              key={establecimiento.establecimiento_id}
              establecimiento={establecimiento}
            />
          ))}

          <EstablecimientoForm />
        </Stack>
      </Container>

      <Stack
        direction="row"
        justifyContent="end"
        alignItems="center"
        spacing={1}
      >
        {!stepper.isFirstStep && (
          <Button variant="outlined" size="small" onClick={stepper.back}>
            Atrás
          </Button>
        )}

        <Button variant="contained" size="small" onClick={handleNext}>
          Siguiente
        </Button>
      </Stack>
    </Stack>
  );
}
